import { Helmet } from "react-helmet-async";
import { BRAND } from "@/data/content";

const SITE_URL = "https://aftabazad.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/aftab-azad.jpg`;

// Person + ProfessionalService structured data (helps entity/expert SEO).
const PERSON_LD = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: BRAND.name,
  jobTitle: "Executive Technology Advisor & Fractional CIO",
  description:
    "Executive Technology Advisor and Fractional CIO to CEOs, boards, growing businesses and private equity. Technology strategy, digital transformation and AI advisory across India, UAE, the Gulf Region, Singapore and globally.",
  url: SITE_URL,
  image: DEFAULT_OG_IMAGE,
  email: `mailto:${BRAND.email}`,
  knowsAbout: [
    "Fractional CIO",
    "Virtual CIO",
    "Executive Technology Advisory",
    "Board Technology Advisor",
    "Digital Transformation",
    "Technology Strategy",
    "AI Strategy",
    "Enterprise Technology Leadership",
    "ERP & SAP Advisory",
    "Technology Due Diligence",
    "Mid-Market Technology Leadership",
    "Family-Owned Business Advisory",
  ],
  areaServed: [
    "India",
    "United Arab Emirates",
    "Gulf Region",
    "Middle East",
    "Singapore",
    "Global",
  ],
  sameAs: [BRAND.socials.linkedin, BRAND.socials.x, BRAND.socials.youtube].filter(
    (u) => u && u !== "#",
  ),
};

const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: `${BRAND.name} Advisory`,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  image: DEFAULT_OG_IMAGE,
  founder: { "@type": "Person", name: BRAND.name },
  description:
    "Independent, board-level technology advisory for growing businesses, mid-market enterprises, family-owned groups and private equity across India, the UAE and the Gulf Region.",
  areaServed: [
    "India",
    "United Arab Emirates",
    "Gulf Region",
    "Middle East",
    "Singapore",
    "Global",
  ],
  serviceType: [
    "Fractional CIO",
    "Executive Technology Advisory",
    "Technology Strategy",
    "Digital Transformation",
    "AI Strategy",
    "ERP & SAP Advisory",
  ],
};

export default function Seo({
  title,
  description,
  path = "",
  keywords = [],
  type = "website",
  image = DEFAULT_OG_IMAGE,
  jsonLd = null,
}) {
  const fullTitle = title
    ? `${title} | ${BRAND.name}`
    : `${BRAND.name} | Executive Technology Advisor & Fractional CIO`;
  const desc = description || BRAND.tagline;
  const url = `${SITE_URL}${path}`;
  const kw = keywords.length
    ? keywords.join(", ")
    : "Executive Technology Advisor, Fractional CIO, Virtual CIO, Board Technology Advisor, Digital Transformation Advisor, Technology Strategy Consultant, AI Strategy Consultant, Enterprise Technology Leadership";
  const safeImage = image || DEFAULT_OG_IMAGE;
  const ogImage = safeImage.startsWith("http") ? safeImage : `${SITE_URL}${safeImage}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <meta name="keywords" content={kw} />
      <meta name="author" content={BRAND.name} />
      <meta name="robots" content="index, follow, max-image-preview:large" />

      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />

      <link rel="canonical" href={url} />

      <script type="application/ld+json">{JSON.stringify(PERSON_LD)}</script>
      <script type="application/ld+json">{JSON.stringify(ORGANIZATION_LD)}</script>
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
